import React, { useEffect } from "react";
import { X, Heart, Users, ExternalLink } from "lucide-react";

const FB_URL = "https://www.facebook.com/share/1Xdc5atMdD/";
const TIKTOK_URL = "https://www.tiktok.com/@rj.than4";
const LOGO_URL = "/assets/topmada-logo.png";

// Chiffres confirmés par Top Mada (modifiables ici à la main quand ça grimpe)
const TIKTOK_FOLLOWERS = "52,9K";
const TIKTOK_LIKES = "490,5K";

/**
 * Carte modale "Top Mada" — s'affiche au clic du badge "by Top Mada".
 * Présente l'admin / curateur, ses stats sociales et les liens FB & TikTok.
 */
export const TopMadaCard = ({ open, onClose }) => {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    document.addEventListener("keydown", onKey);
    // lock scroll
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      data-testid="topmada-card-overlay"
      onClick={onClose}
      className="fixed inset-0 z-[80] flex items-center justify-center px-4 py-6 bg-black/70 backdrop-blur-sm animate-fade-up"
    >
      <div
        data-testid="topmada-card"
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-md rounded-3xl overflow-hidden border border-white/10 shadow-2xl"
        style={{ background: "linear-gradient(180deg,#1a1410 0%,#0F0C0B 100%)" }}
      >
        {/* halo doré derrière */}
        <div className="pointer-events-none absolute inset-0 opacity-60"
             style={{ background: "radial-gradient(380px 220px at 50% 0%, rgba(255,170,40,0.25), transparent 70%)" }} />

        {/* close */}
        <button
          type="button"
          onClick={onClose}
          aria-label="Fermer"
          data-testid="topmada-card-close"
          className="absolute top-3 right-3 z-10 p-2 rounded-full text-white/70 hover:text-white hover:bg-white/10 transition"
        >
          <X size={18} />
        </button>

        <div className="relative p-6 pb-5 text-center">
          {/* logo doré circulaire */}
          <div className="mx-auto w-28 h-28 rounded-full overflow-hidden ring-2 ring-amber-300/60 shadow-[0_0_40px_-5px_rgba(255,170,40,0.6)] bg-black">
            <img
              src={LOGO_URL}
              alt="Top Mada"
              className="w-full h-full object-cover"
              data-testid="topmada-card-logo"
            />
          </div>

          <h3 className="mt-4 text-2xl font-extrabold text-white tracking-tight">
            Top Mada <span className="ml-1">🎶</span>
          </h3>
          <p className="text-amber-200/90 text-xs font-semibold mt-0.5">@rj.than4</p>
          <p className="mt-3 text-sm text-white/80 leading-snug">
            Toutes les Top meilleures musiques de Mada 🇲🇬<br />
            Curateur officiel & propulseur de <span className="text-orange-300 font-semibold">Voamada</span>.
          </p>

          {/* stats */}
          <div className="grid grid-cols-2 gap-3 mt-5">
            <div className="rounded-2xl bg-white/[0.05] border border-white/10 px-3 py-3"
                 data-testid="topmada-card-followers">
              <div className="flex items-center justify-center gap-1.5 text-amber-200 text-xs font-semibold uppercase tracking-wider">
                <Users size={12} /> Followers
              </div>
              <div className="mt-1 text-2xl font-extrabold text-white">{TIKTOK_FOLLOWERS}</div>
              <div className="text-[10px] text-white/50">sur TikTok</div>
            </div>
            <div className="rounded-2xl bg-white/[0.05] border border-white/10 px-3 py-3"
                 data-testid="topmada-card-likes">
              <div className="flex items-center justify-center gap-1.5 text-pink-300 text-xs font-semibold uppercase tracking-wider">
                <Heart size={12} /> J'aime
              </div>
              <div className="mt-1 text-2xl font-extrabold text-white">{TIKTOK_LIKES}</div>
              <div className="text-[10px] text-white/50">cumulés</div>
            </div>
          </div>

          {/* CTAs */}
          <div className="mt-5 grid grid-cols-1 gap-2.5">
            <a
              href={TIKTOK_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="topmada-card-tiktok-btn"
              className="relative w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-2xl font-bold text-black bg-white shadow-lg hover:scale-[1.02] transition-transform overflow-hidden"
            >
              <span className="absolute -left-1 top-0 bottom-0 w-1 bg-[#25F4EE]" />
              <span className="absolute -right-1 top-0 bottom-0 w-1 bg-[#FE2C55]" />
              <TikTokIcon className="w-5 h-5" />
              Suivre sur TikTok
              <ExternalLink size={14} className="opacity-50" />
            </a>
            <a
              href={FB_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="topmada-card-fb-btn"
              className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-2xl font-bold text-white transition hover:brightness-110"
              style={{ background: "#1877F2" }}
            >
              <FacebookIcon className="w-5 h-5" />
              Page Facebook
              <ExternalLink size={14} className="opacity-70" />
            </a>
          </div>

          <a
            href="/topmada"
            data-testid="topmada-card-charts-btn"
            className="block mt-4 text-center text-sm font-bold text-amber-200 hover:text-amber-100 underline-offset-4 hover:underline"
          >
            🏆 Voir les Charts Top Mada
          </a>
          <p className="mt-3 text-[11px] text-white/40">
            Tu viens de TikTok ? Découvre <span className="text-orange-300 font-semibold">Voamada</span> — la plateforme officielle des talents malgaches.
          </p>
        </div>
      </div>
    </div>
  );
};

// Icônes inline (pas de dépendance supplémentaire)
const TikTokIcon = ({ className = "" }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.32a8.16 8.16 0 0 0 4.77 1.52V6.4a4.85 4.85 0 0 1-1.84-.04Z"/>
  </svg>
);

const FacebookIcon = ({ className = "" }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M22 12a10 10 0 1 0-11.56 9.88v-6.99H7.9V12h2.54V9.8c0-2.51 1.49-3.9 3.78-3.9 1.09 0 2.24.2 2.24.2v2.46h-1.26c-1.24 0-1.63.77-1.63 1.56V12h2.77l-.44 2.89h-2.33v6.99A10 10 0 0 0 22 12Z"/>
  </svg>
);

export default TopMadaCard;
